import { computed, isSignal, signal, Signal } from '@angular/core';
import {
  PartialStateUpdater,
  signalStoreFeature,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { isNullish } from '@ts-helpers/nullish';
import { Prettify } from '@ts-helpers/prettify';

type ListFields<State> = Prettify<{
  [K in keyof State as State[K] extends ReadonlyArray<string | number>
    ? K
    : never]: State[K] extends ReadonlyArray<infer U> ? U : never;
}>;

type ErrorField = 'loadingErrors' | 'updatingErrors' | 'creatingErrors' | 'deletingErrors';
type SignalStateArg<Key> = Key | 'all' | Signal<Key>;

export type CallStatusState<ID extends string | number> = {
  idsInLoading: ReadonlyArray<string>;
  idsInCreating: ReadonlyArray<string>;
  idsInUpdating: ReadonlyArray<string>;
  idsInDeleting: ReadonlyArray<string>;

  createdMap: Record<string, ID>;

  // errors
  loadingErrors: Record<string, Error>;
  updatingErrors: Record<string, Error>;
  creatingErrors: Record<string, Error>;
  deletingErrors: Record<string, Error>;
};

export type CallStatus = 'start' | 'success' | Error;

export function withCallStatus<EntityId extends string | number = string>() {
  const initialState: CallStatusState<EntityId> = {
    idsInLoading: [],
    idsInUpdating: [],
    idsInDeleting: [],
    idsInCreating: [],

    createdMap: {},

    loadingErrors: {},
    updatingErrors: {},
    creatingErrors: {},
    deletingErrors: {},
  };

  return signalStoreFeature(
    withState(initialState),

    withMethods(
      ({
        idsInLoading,
        idsInCreating,
        idsInUpdating,
        idsInDeleting,
        loadingErrors,
        updatingErrors,
        creatingErrors,
        deletingErrors,
        createdMap,
      }) => ({
        isLoading(input: SignalStateArg<EntityId> = 'all'): Signal<boolean> {
          const id = isSignal(input) ? input : signal(input);
          return computed(() => idsInLoading().includes(id().toString()));
        },
        isUpdating(input: SignalStateArg<EntityId> = 'all'): Signal<boolean> {
          const id = isSignal(input) ? input : signal(input);
          return computed(() => idsInUpdating().includes(id().toString()));
        },
        isCreating(input: SignalStateArg<string> = 'all'): Signal<boolean> {
          const id = isSignal(input) ? input : signal(input);
          return computed(() => idsInCreating().includes(id()));
        },
        isDeleting(input: SignalStateArg<EntityId> = 'all'): Signal<boolean> {
          const id = isSignal(input) ? input : signal(input);
          return computed(() => idsInDeleting().includes(id().toString()));
        },

        created(input: SignalStateArg<string> = 'all'): Signal<EntityId | undefined> {
          const tempId = isSignal(input) ? input : signal(input);
          return computed(() => createdMap()[tempId()]);
        },

        loadingError(input: SignalStateArg<EntityId> = 'all'): Signal<Error | undefined> {
          const id = isSignal(input) ? input : signal(input);
          return computed(() => loadingErrors()[id().toString()]);
        },
        updatingError(input: SignalStateArg<EntityId> = 'all'): Signal<Error | undefined> {
          const id = isSignal(input) ? input : signal(input);
          return computed(() => updatingErrors()[id().toString()]);
        },
        creatingError(input: SignalStateArg<string> = 'all'): Signal<Error | undefined> {
          const id = isSignal(input) ? input : signal(input);
          return computed(() => creatingErrors()[id()]);
        },
        deletingError(input: SignalStateArg<EntityId> = 'all'): Signal<Error | undefined> {
          const id = isSignal(input) ? input : signal(input);
          return computed(() => deletingErrors()[id().toString()]);
        },
      })
    ),
    withComputed(({ idsInLoading, idsInCreating, idsInUpdating, idsInDeleting }) => ({
      someLoading: computed(() => idsInLoading().length > 0),
      someUpdating: computed(() => idsInUpdating().length > 0),
      someDeleting: computed(() => idsInDeleting().length > 0),
      someCreating: computed(() => idsInCreating().length > 0),
    }))
  );
}

function removeField<T>(state: T, field: keyof T): T {
  const { [field]: _, ...rest } = state;
  return rest as T;
}

export function setCallStatus<State extends CallStatusState<string | number>>(config: {
  field?: keyof ListFields<State>;
  id?: State extends CallStatusState<infer ID> ? ID : undefined;
  tempId?: string;
  status: CallStatus;
  errorPrefix?: string;
}): PartialStateUpdater<State> {
  const { tempId = 'all', field = 'idsInLoading', status } = config;

  if (field === 'idsInCreating') {
    if (status instanceof Error) {
      return state => ({
        ...state,
        idsInCreating: state.idsInCreating.filter(existsId => existsId !== tempId),
        creatingErrors: { ...state.creatingErrors, [tempId]: status },
      });
    } else if (status === 'success') {
      const id = isNullish(config.id) ? 'many id' : config.id;
      return state => ({
        ...state,
        idsInCreating: state.idsInCreating.filter(existsId => existsId !== tempId),
        createdMap: { ...state.createdMap, [tempId]: id },
      });
    } else {
      return state => ({
        ...state,
        idsInCreating: [...state.idsInCreating, tempId],
        creatingErrors: removeField(state.creatingErrors, tempId),
      });
    }
  } else {
    const id = isNullish(config.id) ? 'all' : config.id.toString();
    let errorField: ErrorField = 'loadingErrors';
    if (field === 'idsInUpdating') errorField = 'updatingErrors';
    if (field === 'idsInDeleting') errorField = 'deletingErrors';
    const errorId = `${config.errorPrefix ?? ''}${id}`;
    if (status instanceof Error) {
      return state => ({
        ...state,
        [field]: (state[field] as ReadonlyArray<State[keyof ListFields<State>] | 'all'>).filter(
          existsId => existsId !== id
        ),
        [errorField]: { ...state[errorField], [errorId]: status },
      });
    } else if (status === 'success') {
      return state => ({
        ...state,
        [field]: (state[field] as ReadonlyArray<State[keyof ListFields<State>] | 'all'>).filter(
          existsId => existsId !== id
        ),
      });
    } else {
      return state => ({
        ...state,
        [field]: [...(state[field] as ReadonlyArray<State[keyof ListFields<State>] | 'all'>), id],
        [errorField]: removeField(state[errorField], errorId),
      });
    }
  }
}

export function setLoading<State extends CallStatusState<string | number>>(
  id?: State extends CallStatusState<infer ID> ? ID : undefined,
  field?: keyof ListFields<State>,
  errorPrefix = ''
): PartialStateUpdater<State> {
  return setCallStatus({ field, id, status: 'start', errorPrefix });
}

export function setLoaded<State extends CallStatusState<string | number>>(
  id?: State extends CallStatusState<infer ID> ? ID : undefined,
  field?: keyof ListFields<State>
): PartialStateUpdater<State> {
  return setCallStatus({ field, id, status: 'success' });
}

export function setLoadingError<State extends CallStatusState<string | number>>(
  error: Error,
  id?: State extends CallStatusState<infer ID> ? ID : undefined,
  field?: keyof ListFields<State>,
  errorPrefix?: string
): PartialStateUpdater<State> {
  return setCallStatus({ field, id, status: error, errorPrefix });
}

export function setCreating<State extends CallStatusState<string | number>>(
  tempId?: string
): PartialStateUpdater<State> {
  return setCallStatus({
    field: 'idsInCreating' as keyof ListFields<State>,
    tempId,
    status: 'start',
  });
}

export function setCreated<State extends CallStatusState<string | number>>(
  tempId?: string,
  id?: State extends CallStatusState<infer ID> ? ID : undefined
): PartialStateUpdater<State> {
  return setCallStatus({
    field: 'idsInCreating' as keyof ListFields<State>,
    tempId,
    id,
    status: 'success',
  });
}

export function setCreatingError<State extends CallStatusState<string | number>>(
  error: Error,
  tempId?: string
): PartialStateUpdater<State> {
  return setCallStatus({
    field: 'idsInCreating' as keyof ListFields<State>,
    tempId,
    status: error,
  });
}

export function setUpdating<State extends CallStatusState<string | number>>(
  id?: State extends CallStatusState<infer ID> ? ID : undefined
): PartialStateUpdater<State> {
  return setCallStatus({ field: 'idsInUpdating' as keyof ListFields<State>, id, status: 'start' });
}

export function setUpdated<State extends CallStatusState<string | number>>(
  id?: State extends CallStatusState<infer ID> ? ID : undefined
): PartialStateUpdater<State> {
  return setCallStatus({
    field: 'idsInUpdating' as keyof ListFields<State>,
    id,
    status: 'success',
  });
}

export function setUpdatingError<State extends CallStatusState<string | number>>(
  error: Error,
  id?: State extends CallStatusState<infer ID> ? ID : undefined
): PartialStateUpdater<State> {
  return setCallStatus({ field: 'idsInUpdating' as keyof ListFields<State>, id, status: error });
}

export function setDeleting<State extends CallStatusState<string | number>>(
  id?: State extends CallStatusState<infer ID> ? ID : undefined
): PartialStateUpdater<State> {
  return setCallStatus({ field: 'idsInDeleting' as keyof ListFields<State>, id, status: 'start' });
}

export function setDeleted<State extends CallStatusState<string | number>>(
  id?: State extends CallStatusState<infer ID> ? ID : undefined
): PartialStateUpdater<State> {
  return setCallStatus({
    field: 'idsInDeleting' as keyof ListFields<State>,
    id,
    status: 'success',
  });
}

export function setDeletingError<State extends CallStatusState<string | number>>(
  error: Error,
  id?: State extends CallStatusState<infer ID> ? ID : undefined
): PartialStateUpdater<State> {
  return setCallStatus({ field: 'idsInDeleting' as keyof ListFields<State>, id, status: error });
}

export type CallStatusStore<EntityId extends string | number> = ReturnType<
  ReturnType<typeof withCallStatus<EntityId>>
>['stateSignals'] &
  ReturnType<ReturnType<typeof withCallStatus<EntityId>>>['methods'] &
  ReturnType<ReturnType<typeof withCallStatus<EntityId>>>['props'];
